import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import Head from '@/components/base/Head';
import DentalPriceTable from './components/DentalPriceTable';
import SurgeryPriceTable from './components/SurgeryPriceTable';
import HairTransplantPriceTable from './components/HairTransplantPriceTable';
import { getSavingsComparisons, buildDentalSchema, buildSurgerySchema, buildHairSchema } from '@/lib/treatmentStore';
import { useAdminImage } from '@/hooks/useAdminImage';

const faqKeys = [
  { q: 'svc.pricing_faq1_q', a: 'svc.pricing_faq1_a' },
  { q: 'svc.pricing_faq2_q', a: 'svc.pricing_faq2_a' },
  { q: 'svc.pricing_faq3_q', a: 'svc.pricing_faq3_a' },
  { q: 'svc.pricing_faq4_q', a: 'svc.pricing_faq4_a' },
  { q: 'svc.pricing_faq5_q', a: 'svc.pricing_faq5_a' },
];

const PKG_ESSENTIAL_DEFAULT = 'https://readdy.ai/api/search-image?query=Clean%20modern%20beauty%20treatment%20room%20with%20soft%20natural%20daylight%20streaming%20through%20large%20windows%2C%20minimalist%20white%20interior%20with%20subtle%20warm%20beige%20tones%2C%20professional%20treatment%20bed%20with%20crisp%20white%20linens%2C%20small%20wooden%20side%20table%20with%20organic%20skincare%20bottles%2C%20potted%20green%20plant%20in%20ceramic%20vase%2C%20calm%20serene%20atmosphere%2C%20no%20text%20no%20logos%20no%20labels%2C%20editorial%20photography%20style%20with%20soft%20shadows&width=600&height=500&seq=pkg-essential-v2&orientation=portrait';
const PKG_PREMIUM_DEFAULT = 'https://readdy.ai/api/search-image?query=Premium%20aesthetic%20clinic%20treatment%20suite%20with%20professional%20overhead%20track%20lighting%2C%20high-end%20medical%20equipment%20with%20polished%20metallic%20finishes%2C%20glossy%20white%20countertops%20with%20subtle%20reflections%2C%20lush%20monstera%20plant%20in%20elegant%20ceramic%20pot%2C%20warm%20cream%20and%20beige%20walls%2C%20clean%20professional%20healthcare%20interior%20design%2C%20no%20text%20no%20logos%20no%20labels%2C%20realistic%20editorial%20photography&width=600&height=500&seq=pkg-premium-v2&orientation=portrait';
const PKG_LUXE_DEFAULT = 'https://readdy.ai/api/search-image?query=Luxurious%20high-end%20clinic%20room%20with%20natural%20daylight%20filtering%20through%20cream%20linen%20curtains%2C%20elegant%20cognac%20leather%20treatment%20chair%20with%20natural%20grain%20texture%2C%20dove%20gray%20walls%20with%20soft%20warm%20undertones%2C%20professional%20aesthetic%20devices%20with%20sleek%20industrial%20design%2C%20marble%20accent%20table%2C%20calm%20sophisticated%20atmosphere%2C%20no%20text%20no%20logos%20no%20labels%2C%20editorial%20photography%20with%20soft%20diffused%20lighting&width=600&height=500&seq=pkg-luxe-v2&orientation=portrait';
const PKG_EXCLUSIF_DEFAULT = 'https://readdy.ai/api/search-image?query=Exclusive%20VIP%20beauty%20suite%20with%20fresh%20white%20peonies%20in%20crystal%20vase%20on%20marble%20side%20table%2C%20soft%20natural%20daylight%20from%20floor%20to%20ceiling%20windows%20creating%20gentle%20shadows%2C%20premium%20velvet%20furnishings%20in%20warm%20neutral%20tones%2C%20gold%20accent%20details%2C%20elegant%20mirrors%20with%20subtle%20frames%2C%20luxurious%20yet%20authentic%20atmosphere%2C%20no%20text%20no%20logos%20no%20labels%2C%20high-end%20editorial%20photography%20style&width=600&height=500&seq=pkg-exclusif-v2&orientation=portrait';

export default function PricingPage() {
  const { t } = useTranslation();
  const location = useLocation();

  const heroBg = useAdminImage(
    'pricing',
    'images.pricing.hero_bg',
    'https://readdy.ai/api/search-image?query=Luxurious%20beauty%20clinic%20consultation%20room%20with%20warm%20lighting%2C%20elegant%20minimalist%20interior%2C%20cream%20and%20beige%20tones%2C%20comfortable%20chairs%2C%20professional%20yet%20welcoming%20atmosphere%2C%20soft%20ambient%20glow%2C%20high-end%20medical%20aesthetic%20clinic%20design&width=1800&height=920&seq=pricing-hero-1&orientation=landscape'
  );
  const essentialImg = useAdminImage('pricing', 'images.pricing.essential_img', PKG_ESSENTIAL_DEFAULT);
  const premiumImg = useAdminImage('pricing', 'images.pricing.premium_img', PKG_PREMIUM_DEFAULT);
  const luxeImg = useAdminImage('pricing', 'images.pricing.luxe_img', PKG_LUXE_DEFAULT);
  const exclusifImg = useAdminImage('pricing', 'images.pricing.exclusif_img', PKG_EXCLUSIF_DEFAULT);

  const packages = [
    {
      key: 'basic',
      color: 'bg-secondary-100',
      accent: 'text-secondary-700',
      accentBg: 'bg-secondary-500',
      image: essentialImg,
    },
    {
      key: 'premium',
      color: 'bg-primary-100',
      accent: 'text-primary-700',
      accentBg: 'bg-primary-500',
      image: premiumImg,
    },
    {
      key: 'luxe',
      color: 'bg-accent-100',
      accent: 'text-accent-700',
      accentBg: 'bg-accent-500',
      image: luxeImg,
    },
    {
      key: 'exclusive',
      color: 'bg-secondary-200',
      accent: 'text-secondary-800',
      accentBg: 'bg-secondary-600',
      image: exclusifImg,
    },
  ];

  const savingsComparisons = getSavingsComparisons();
  const faqs = faqKeys.map((f) => ({ q: t(f.q), a: t(f.a) }));

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      }
    }
  }, [location]);

  const dentalSchemaJson = JSON.stringify(buildDentalSchema());
  const surgerySchemaJson = JSON.stringify(buildSurgerySchema());
  const hairSchemaJson = JSON.stringify(buildHairSchema());
  const breadcrumbJson = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://estevaclinic.com/' },
      { '@type': 'ListItem', position: 2, name: 'Preise', item: 'https://estevaclinic.com/pricing' },
    ],
  });
  const faqJson = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: { '@type': 'Answer', text: faq.a },
    })),
  });

  return (
    <>
      <Head
        title={t('seo.pricing.title')}
        metas={[
          { name: 'description', content: t('seo.pricing.description') },
          { name: 'keywords', content: t('seo.pricing.keywords') },
          { name: 'last-modified', content: '2026-06-09' },
          { property: 'og:title', content: t('seo.pricing.title') },
          { property: 'og:description', content: t('seo.pricing.description') },
          { property: 'og:url', content: 'https://estevaclinic.com/pricing' },
          { property: 'og:type', content: 'website' },
          { property: 'og:locale', content: t('seo.ogLocale') },
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: t('seo.pricing.title') },
          { name: 'twitter:description', content: t('seo.pricing.description') },
        ]}
        links={[
          { rel: 'canonical', href: 'https://estevaclinic.com/pricing' },
        ]}
        scripts={[
          { type: 'application/ld+json', children: dentalSchemaJson },
          { type: 'application/ld+json', children: surgerySchemaJson },
          { type: 'application/ld+json', children: hairSchemaJson },
          { type: 'application/ld+json', children: breadcrumbJson },
          { type: 'application/ld+json', children: faqJson },
        ]}
      />

      <main>
        <Navbar />

        <section className="relative min-h-[380px] md:min-h-[460px] flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${heroBg})`,
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/55"></div>
          <div className="relative z-10 text-center px-4 sm:px-6 w-full max-w-4xl mx-auto">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-white/90 text-xs tracking-[0.2em] uppercase font-label border border-white/20">
              {t('svc.pricing_hero_tag')}
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mt-6 leading-[1.1]">
              {t('pricing.heading_line1')}{' '}
              <span className="font-light italic">{t('pricing.heading_line2')}</span>
            </h1>
            <p className="mt-4 text-white/70 text-sm md:text-base max-w-xl mx-auto font-body leading-relaxed break-words">
              {t('pricing.subtitle')}
            </p>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-10 bg-primary-500">
          <div className="max-w-4xl mx-auto">
            <h3 className="font-heading text-lg md:text-xl font-bold text-white text-center mb-6">
              {t('pricing.included_title')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="flex items-start gap-4 p-4 md:p-5 rounded-xl bg-white/10 border border-white/15">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full bg-white/20">
                  <i className="ri-hotel-line text-white text-lg"></i>
                </div>
                <div>
                  <p className="text-sm md:text-base text-white font-body font-semibold">&ge; &euro;3.000</p>
                  <p className="text-xs md:text-sm text-white/80 font-body leading-relaxed mt-1">
                    {t('pricing.included_over_3000')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 md:p-5 rounded-xl bg-white/10 border border-white/15">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full bg-white/20">
                  <i className="ri-taxi-line text-white text-lg"></i>
                </div>
                <div>
                  <p className="text-sm md:text-base text-white font-body font-semibold">&ge; &euro;1.500</p>
                  <p className="text-xs md:text-sm text-white/80 font-body leading-relaxed mt-1">
                    {t('pricing.included_over_1500')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <DentalPriceTable />

        <SurgeryPriceTable />

        {/* Savings Comparison */}
        <section className="py-16 md:py-24 px-4 md:px-10 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-[10px] md:text-xs tracking-[0.15em] uppercase font-label">
                <i className="ri-money-euro-circle-line text-xs"></i>
                {t('svc.savings_tag')}
              </span>
              <h2 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-900">
                {t('svc.savings_title')}
              </h2>
              <p className="mt-3 text-sm text-foreground-500 font-body max-w-lg mx-auto leading-relaxed break-words">
                {t('svc.savings_subtitle')}
              </p>
            </div>

            <div className="rounded-2xl border border-background-200/70 overflow-hidden bg-background-50">
              <div className="hidden md:grid grid-cols-12 text-[10px] tracking-[0.15em] uppercase text-foreground-400 font-label py-3.5 px-6 border-b border-background-200/70 bg-background-100">
                <span className="col-span-5">{t('svc.savings_col_treatment')}</span>
                <span className="col-span-3 text-right">D / AT / CH</span>
                <span className="col-span-2 text-right">Esteva Clinic</span>
                <span className="col-span-2 text-right">{t('svc.savings_col_savings')}</span>
              </div>

              <div className="divide-y divide-background-200/60">
                {savingsComparisons.map((item, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-1 md:grid-cols-12 py-3.5 md:py-4 px-4 md:px-6 text-sm font-body hover:bg-background-100/50 transition-colors items-center gap-2 md:gap-0"
                  >
                    <span className="md:col-span-5 font-label text-foreground-900 font-semibold text-xs md:text-sm">{item.procedure}</span>
                    <span className="md:col-span-3 md:text-right text-foreground-500 text-xs md:text-sm line-through decoration-foreground-300">
                      {item.dePrice}
                    </span>
                    <span className="md:col-span-2 md:text-right text-primary-600 font-heading font-bold text-xs md:text-sm">
                      {item.alPrice}
                    </span>
                    <span className="md:col-span-2 md:text-right">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-[10px] md:text-xs font-label font-semibold">
                        {item.savings}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <HairTransplantPriceTable />

        {/* Package Deals */}
        <section className="py-16 md:py-24 px-4 md:px-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-[10px] md:text-xs tracking-[0.15em] uppercase font-label">
                <i className="ri-stack-line text-xs"></i>
                {t('svc.packages_tag')}
              </span>
              <h2 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-900">
                {t('pricing.heading_line1')}{' '}
                <span className="font-light italic">{t('pricing.heading_line2')}</span>
              </h2>
              <p className="mt-3 text-sm text-foreground-500 font-body max-w-lg mx-auto leading-relaxed">
                {t('pricing.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {packages.map((pkg) => (
                <div
                  key={pkg.key}
                  className="group relative rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 transition-all duration-500 hover:scale-[1.02] hover:border-primary-200 cursor-pointer flex flex-col"
                >
                  <div className="relative h-40 sm:h-48 md:h-56 overflow-hidden">
                    <img
                      src={pkg.image}
                      alt={t(`pricing.${pkg.key}.title`)}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>
                  </div>

                  <div className="p-4 md:p-5 lg:p-6 flex flex-col flex-1">
                    <h3 className="font-heading text-lg md:text-xl lg:text-2xl font-semibold text-foreground-900 leading-snug">
                      {t(`pricing.${pkg.key}.title`)}
                    </h3>
                    <p className={`text-base md:text-lg lg:text-xl font-heading font-bold mt-1 ${pkg.accent}`}>
                      {t(`pricing.${pkg.key}.price`)}
                    </p>

                    <ul className="mt-3 md:mt-4 space-y-2 md:space-y-2.5 flex-1">
                      {[1, 2, 3].map((f) => (
                        <li key={f} className="flex items-start gap-2 md:gap-2.5 text-xs md:text-sm text-foreground-600 font-body leading-snug">
                          <span className="w-4 h-4 md:w-5 md:h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-primary-500 text-xs md:text-sm"></i>
                          </span>
                          <span>{t(`pricing.${pkg.key}.feature${f}`)}</span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to="/booking"
                      className={`mt-4 md:mt-5 inline-flex items-center justify-center gap-1.5 md:gap-2 w-full py-2 md:py-2.5 rounded-full text-white text-[11px] md:text-xs tracking-[0.1em] md:tracking-[0.12em] uppercase font-label font-semibold hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap ${pkg.accentBg}`}
                    >
                      {t('pricing.cta')}
                      <i className="ri-arrow-right-line text-xs"></i>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 md:py-28 px-6 md:px-10 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 text-center">
              {t('svc.faq_heading')}
            </h2>
            <div className="mt-10 space-y-4">
              {faqs.map((faq, idx) => (
                <details key={idx} className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 md:px-6 py-4 cursor-pointer font-label text-sm md:text-base text-foreground-900 font-semibold list-none">
                    {faq.q}
                    <i className="ri-add-line text-lg text-foreground-400 group-open:hidden"></i>
                    <i className="ri-subtract-line text-lg text-primary-500 hidden group-open:block"></i>
                  </summary>
                  <div className="px-5 md:px-6 pb-4">
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed font-body">
                      {faq.a}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 px-6 md:px-10 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
              {t('service_detail.cta_heading')}
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-500 font-body leading-relaxed break-words">
              {t('svc.pricing_cta_subtitle')}
            </p>
            <Link
              to="/booking"
              className="mt-7 inline-block px-8 py-3.5 rounded-full bg-primary-500 text-white text-sm tracking-[0.1em] uppercase font-label hover:bg-primary-600 transition-colors cursor-pointer no-underline whitespace-nowrap"
            >
              {t('pricing.cta')}
            </Link>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}